import { Box, IconButton } from "@mui/material";
import { DownArrowIcon } from "components";
import { useAtom, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { integrationCategories } from "../constants/chipData";
import { formDataAtom, otherErrorsAtom, updateFieldAtom } from "../store/onboardingModalStore";
import { OnboardingCategoryGroup } from "./shared/OnboardingCategoryGroup";
import { OnboardingStepLayout } from "./shared/OnboardingStepLayout";

export const OnboardingIntegrations = (): JSX.Element | null => {
  const [formData] = useAtom(formDataAtom);
  const updateField = useSetAtom(updateFieldAtom);
  const [otherErrors, setOtherErrors] = useAtom(otherErrorsAtom);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const checkScrollable = useCallback(() => {
    const scrollParent = contentRef.current?.parentElement;
    if (!scrollParent) return;
    const hasMore =
      scrollParent.scrollHeight -
        scrollParent.scrollTop -
        scrollParent.clientHeight >
      10;
    setShowScrollButton(hasMore);
  }, []);

  useEffect(() => {
    const scrollParent = contentRef.current?.parentElement;
    if (!scrollParent) return;
    checkScrollable();
    scrollParent.addEventListener("scroll", checkScrollable);
    return () => scrollParent.removeEventListener("scroll", checkScrollable);
  }, [checkScrollable]);

  const handleScrollDown = (): void => {
    const scrollParent = contentRef.current?.parentElement;
    if (!scrollParent) return;
    scrollParent.scrollBy({ top: 300, behavior: "smooth" });
  };

  const orgType = formData.orgType;
  if (!orgType) return null;

  const categories = integrationCategories[orgType];

  const handleToggle = (categoryKey: string, value: string): void => {
    const currentIntegrations = { ...formData.integrations };
    const categoryValues = currentIntegrations[categoryKey] ?? [];

    if (value === "none") {
      currentIntegrations[categoryKey] = categoryValues.includes("none")
        ? categoryValues.filter((v) => v !== "none")
        : ["none"];
      updateField({ integrations: currentIntegrations });
      const otherKey = `integration_${categoryKey}`;
      const { [otherKey]: _, ...rest } = formData.otherTexts;
      updateField({ otherTexts: rest });
      return;
    }

    const withoutNone = categoryValues.filter((v) => v !== "none");
    currentIntegrations[categoryKey] = withoutNone.includes(value)
      ? withoutNone.filter((v) => v !== value)
      : [...withoutNone, value];

    updateField({ integrations: currentIntegrations });

    if (value === "other" && categoryValues.includes("other")) {
      const otherKey = `integration_${categoryKey}`;
      const { [otherKey]: _, ...rest } = formData.otherTexts;
      updateField({ otherTexts: rest });
    }
  };

  const handleOtherTextChange = (categoryKey: string, value: string): void => {
    const otherKey = `integration_${categoryKey}`;
    updateField({
      otherTexts: { ...formData.otherTexts, [otherKey]: value },
    });
  };

  return (
    <OnboardingStepLayout
      title="Which integrations matters the most?"
      subtitle="Tell us which tools you're already using that you'd like to connect with DeskDay. This helps us prioritize our integration roadmap."
    >
      <Box
        ref={contentRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {categories.map((category) => (
          <OnboardingCategoryGroup
            key={category.key}
            label={category.label}
            options={category.options}
            selectedValues={formData.integrations[category.key] ?? []}
            onToggle={(value) => handleToggle(category.key, value)}
            otherText={formData.otherTexts[`integration_${category.key}`]}
            onOtherTextChange={(val) =>
              handleOtherTextChange(category.key, val)
            }
            otherError={!!otherErrors[`integration_${category.key}`]}
            onOtherErrorClear={() => {
              const errorKey = `integration_${category.key}`;
              const { [errorKey]: _, ...rest } = otherErrors;
              setOtherErrors(rest);
            }}
          />
        ))}
      </Box>
      {showScrollButton && (
        <IconButton
          onClick={handleScrollDown}
          aria-label="Scroll down for more options"
          sx={{
            position: "sticky",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            backgroundColor: "#35373C",
            boxShadow: "2px 2px 5px 0px #00000033",
            padding: 0,
            "&:hover": {
              backgroundColor: "#45474C",
            },
          }}
        >
          <DownArrowIcon
            style={{
              width: "18px",
              height: "18px",
              color: "white",
            }}
          />
        </IconButton>
      )}
    </OnboardingStepLayout>
  );
};
